import React, { useRef, useState, useEffect } from 'react'
import { createUseStyles } from '../helpers/createStyles'
import { theme } from '../styles/theme'
import cn from 'classnames'
import Arrow from './Arrow'
import debounce from '../helpers/debounce'

const Carousel = ({ children, className, isSimpleGrid = false, showMobilePreview = true, hideFooters, showArrows = true, forceDesktopMode = false, itemsLabel = 'ITEM', hidePaginationLabel = false }) => {
  const classes = useStyles()
  const carouselRef = useRef(null)
  const mobilePreviewRef = useRef(null)
  const [canScrollLeft, setCanScrollLeft] = useState(true)
  const [canScrollRight, setCanScrollRight] = useState(true)
  const [isScrollListenerAdded, setIsScrollListenerAdded] = useState(false)
  const [isMobile, setIsMobile] = useState(true)
  const [selectedChild, setSelectedChild] = useState(null)
  const [carouselContainerClass, setCarouselContainerClass] = useState(classes.carouselContainer)
  const [firstChildInView, setFirstChildInView] = useState(0)
  const [lastChildInView, setLastChildInView] = useState(0)
  const [totalChildren, setTotalChildren] = useState(0)
  const currentChildrenInViewRef = useRef([])
  const mobilePreview = mobilePreviewRef.current

  // Debounced setters using the imported debounce helper
  // This is to prevent the pagination label from updating too frequently
  const debouncedSetFirstAndLastInView = useRef(
    debounce((first, last) => {
      setFirstChildInView(first)
      setLastChildInView(last)
    }, 200)
  ).current

  useEffect(async () => {
    let carousel = carouselRef.current
    let childrenElements = Array.from(carousel.children)

    // Explicitly wait for the carousel to be populated
    // This is a bugfix for the issue where the carousel is empty on first render,
    // and would thus render the label as "0 - 0 of 0"
    if (!carousel || !childrenElements || childrenElements.length === 0) {
      console.log('Carousel is empty, waiting for 3 seconds')
      await new Promise(resolve => setTimeout(resolve, 3000))
    }
    carousel = carouselRef.current
    childrenElements = Array.from(carousel.children)
    if (!carousel || !childrenElements || childrenElements.length === 0) {
      console.error('Carousel is empty')
      return
    }
    console.log('Carousel is populated', carousel)

    const observer = new window.IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const thisIndex = childrenElements.indexOf(entry.target)
        if (thisIndex === -1) return
        if (!entry.isIntersecting) {
          // remove from currentChildrenInViewRef
          if (currentChildrenInViewRef.current.includes(thisIndex)) {
            currentChildrenInViewRef.current = currentChildrenInViewRef.current.filter(index => index !== thisIndex)
          }
        }
        if (entry.isIntersecting) {
          // add to currentChildrenInViewRef
          if (!currentChildrenInViewRef.current.includes(thisIndex)) {
            currentChildrenInViewRef.current.push(thisIndex)
          }
        }
        currentChildrenInViewRef.current.sort((a, b) => a - b)

        const first = currentChildrenInViewRef.current[0] + 1
        const last = currentChildrenInViewRef.current[currentChildrenInViewRef.current.length - 1] + 1
        debouncedSetFirstAndLastInView(first, last)
      })
    }, {
      root: carousel,
      rootMargin: '0px',
      threshold: 0.1 // Adjust this value based on your needs
    })

    setTotalChildren(childrenElements.length)
    if (childrenElements.length > 0) {
      setFirstChildInView(1)
      setLastChildInView(Math.min(3, childrenElements.length))
    }
    childrenElements.forEach(child => observer.observe(child))

    return () => {
      observer.disconnect()
    }
  }, [])

  function handleScroll () {
    const averageWidthOfChildrenDesktop = 400 // Adjust this value based on your items' average width
    const averageWidthOfChildrenMobile = 200 // Adjust this value based on your items' average width
    const averageWidthOfChildren = isMobile ? averageWidthOfChildrenMobile : averageWidthOfChildrenDesktop

    // get first child elements width
    const firstChild = carouselRef.current?.children[0]
    const firstChildWidth = firstChild ? firstChild.offsetWidth : 0
    const widthOfChildren = firstChildWidth || averageWidthOfChildren

    if (carouselRef.current) {
      setCanScrollLeft(carouselRef.current.scrollLeft - widthOfChildren > 0)
      setCanScrollRight(
        carouselRef.current.scrollLeft + widthOfChildren + carouselRef.current.offsetWidth < carouselRef.current.scrollWidth
      )
    }
  }

  function handleResize () {
    if ((window.innerWidth < theme.breakpoints.values.md) && !forceDesktopMode) {
      setIsMobile(true)
    } else {
      setIsMobile(false)
    }
    handleScroll()
  }

  if (carouselRef.current && !isScrollListenerAdded) {
    window.addEventListener('resize', handleResize)
    setIsScrollListenerAdded(true)
    setTimeout(() => {
      handleResize()
    }, 300) // Adjust timeout as needed
  }

  const scroll = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = carouselRef.current.offsetWidth / 1.5 // Adjust scroll amount
      console.log('scrollAmount', scrollAmount)
      carouselRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      })
      setTimeout(() => {
        handleScroll()
      }, 300) // Adjust timeout as needed
    }
  }

  const handleChildClick = (child) => {
    if (!isMobile) return // Do not set selected child on desktop
    setSelectedChild(child)
    setCanScrollLeft(false)
    setCanScrollRight(false)
    if (mobilePreview) {
      mobilePreview.focus()
    }
    if (selectedChild === child) {
      setSelectedChild(null)
      // setCanScrollLeft(true)
      // setCanScrollRight(true)
      handleScroll()
    }
  }

  useEffect(() => {
    if (isMobile && selectedChild) {
      setCarouselContainerClass(`${classes.carouselContainer} mobilePreviewIsActive`)
    } else {
      setCarouselContainerClass(classes.carouselContainer)
    }
  }, [selectedChild])

  return (
    <div className={cn(carouselContainerClass, className, forceDesktopMode && 'forceDesktopMode')}>
      <div className={classes.carouselAbsoluteContainer}>
        {(showArrows && canScrollLeft) && (
          <div
            className={`${classes.gradientOverlay} left ${isMobile && 'isMobile'}`}
            onClick={() => scroll('left')}
          />
        )}
        {(showArrows && canScrollLeft) && (
          <div
            className={`${classes.carouselButton} left ${isMobile && 'isMobile'}`}
            onClick={() => scroll('left')}
          >
            <Arrow direction='left' />
          </div>
        )}
        {(showArrows && canScrollRight) && (
          <div
            className={`${classes.gradientOverlay} right ${isMobile && 'isMobile'}`}
            onClick={() => scroll('right')}
          />
        )}
        {(showArrows && canScrollRight) && (
          <div
            className={`${classes.carouselButton} right ${isMobile && 'isMobile'}`}
            onClick={() => scroll('right')}
          >
            <Arrow direction='right' />
          </div>
        )}

        {(isMobile && showMobilePreview) && (
          <div className={cn('mobilePreviewContainer', classes.mobilePreviewContainer, selectedChild ? 'mobilePreviewIsActive' : '')}>
            <div
              className={cn('mobilePreview', classes.mobilePreview, selectedChild ? 'mobilePreviewIsActive' : '')}
              ref={mobilePreviewRef}
            >
              <span className={cn('mobilePreviewButton', classes.mobilePreviewButton, 'minimise')} />
              {selectedChild && React.cloneElement(selectedChild, { preferHexCode: false, isFocused: true, hideFooter: true, onClick: () => handleChildClick(selectedChild) })}
            </div>
          </div>
        )}
      </div>

      <div
        className={cn(
          'carousel',
          classes.carousel,
          (selectedChild && showMobilePreview) && 'mobilePreviewIsActive',
          hideFooters && classes.hideFooters,
          forceDesktopMode && 'forceDesktopMode',
          isSimpleGrid && 'simpleGrid'
        )} ref={carouselRef}
      >
        {React.Children.map(children, (child) => (
          <div
            className={cn(classes.carouselItem, forceDesktopMode && 'forceDesktopMode')}
          >
            {(isMobile && showMobilePreview) && (
              <span className={classes.mobilePreviewButton} />
            )}
            {child && React.cloneElement(child, { forceDesktopMode, onClick: () => handleChildClick(child) })}
          </div>
        ))}
      </div>
      <div className={classes.paginationLabel}>
        {(carouselRef.current && !hidePaginationLabel && totalChildren > 4) && (
          <span>
            {itemsLabel} {firstChildInView} - {lastChildInView} of {totalChildren}
          </span>
        )}
      </div>
    </div>
  )
}

const useStyles = createUseStyles({
  carouselContainer: {
    width: '100%',
    transition: 'padding 0.3s ease-in-out',
    paddingBottom: 0,
    position: 'relative',
    '&.mobilePreviewIsActive': {
      marginBottom: theme.spacing(12)
    }

  },
  carouselAbsoluteContainer: {
    position: 'absolute',
    // marginLeft: -32,
    width: '100%',
    height: '100%'
    // paddingLeft: 32,
  },
  mobilePreviewContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    width: '100%',
    maxWidth: '100%',
    height: '0%',
    opacity: 0,
    zIndex: 15,
    aspectRatio: '45 / 55',

    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    transition: 'all 0.3s ease-in-out',
    '& img': {
      width: '100%'
    },
    '& .cardForeground': {
      '& div': {
        background: 'none'
      }
    },
    '&.mobilePreviewIsActive': {
      height: '100%',
      opacity: 1
    }
  },
  mobilePreview: {
    // marginLeft: theme.spacing(4),
    flex: 1,
    aspectRatio: '45 / 55',
    // backgroundColor: 'rgba(255, 0, 0, 0.8)',
    // width: 'fit-content',
    // maxWidth: '100%',
    height: '0%',
    width: '100%',
    '&.mobilePreviewIsActive': {
      height: '100%'
      // aspectRatio: '70 / 100',
    },

    ' & > div': {
      width: '100%',
      height: '100%',
      minHeight: '100% !important',
      minWidth: '100%'
    },

    '& .cardBackground': {
      minHeight: '100%'
    },
    '& .cardForeground': {
      minHeight: 'fit-content'
    },

    '& img': {
      width: '100% !important',
      height: '100%',
      objectFit: 'cover'
    }
  },
  carousel: {
    overflowX: 'scroll',
    overflowY: 'hidden',
    scrollBehavior: 'smooth',
    padding: 16,
    maxHeight: 'auto',
    display: 'grid',
    gridAutoFlow: 'column dense',
    gridTemplateRows: 'auto auto',
    gridAutoColumns: '50%',
    gridGap: `${theme.spacing(4)}px ${theme.spacing(2)}px`,

    transition: 'all 0.3s ease-in-out',
    '&.mobilePreviewIsActive': {
      opacity: 0,
      pointerEvents: 'none'
    },

    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    },
    '& div:nth-child(4n + 2)': {
      gridRow: 1
    },
    '&.simpleGrid': {
      ' & div': {
        gridRow: 'auto'
      }
    },

    '&::-webkit-scrollbar': {
      display: 'none'
    },
    [theme.breakpoints.up('md')]: {
      maxHeight: 900,
      gridTemplateRows: '600px',
      gridGap: theme.spacing(4),
      gridAutoColumns: 'auto'
    },
    '&.forceDesktopMode': {
      maxHeight: 900,
      gridTemplateRows: '600px',
      gridGap: theme.spacing(4)
    }
  },

  hideFooters: {
    gridGap: `${theme.spacing(1)}px ${theme.spacing(2)}px`
  },

  carouselButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    border: 'none',
    borderRadius: '50%',
    width: 40,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 5,
    '&.left': {
      left: 0,
      '&.isMobile': {
        marginLeft: theme.spacing(-4)
      },
      [theme.breakpoints.up('md')]: {
        marginLeft: theme.spacing(-8)
      }

    },
    '&.right': {
      right: 0,
      '&.isMobile': {
        marginRight: theme.spacing(-4)
      },

      [theme.breakpoints.up('md')]: {
        right: theme.spacing(-8)
      }
    }
  },
  gradientOverlay: {
    position: 'absolute',
    display: 'block',
    cursor: 'pointer',
    top: 0,
    bottom: 0,
    width: '7.5vw',
    zIndex: 5,
    pointerEvents: 'all',
    '&.left': {
      left: 0,
      background: 'linear-gradient(to right, #F8F7EE, 70%, transparent)',
      '&.isMobile': {
        marginLeft: theme.spacing(-4)
      },
      [theme.breakpoints.up('md')]: {
        marginLeft: theme.spacing(-8)
      }
    },
    '&.right': {
      background: 'linear-gradient(to left, #F8F7EE, 70%, transparent)',
      right: 0,
      [theme.breakpoints.up('md')]: {
        right: theme.spacing(-8)
      }
    },
    '&.isMobile': {
      background: 'none !important'
    }
  },

  mobilePreviewButton: {
    position: 'absolute',
    // top: 0,
    // left: 0,
    width: 28,
    height: 28,
    backgroundColor: '#F9FCF896',
    opacity: 0.4,
    zIndex: 5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    pointerEvents: 'none',
    '&::after': {
      content: '"+"',
      color: theme.colors.black,
      fontSize: 16,
      fontWeight: 'bold',
      textAlign: 'center'
    },
    '&.minimise::after': {
      content: '"-"'
    }
  },

  carouselItem: {
    height: 'fit-content',
    cursor: 'pointer', // Add pointer cursor for clickable items
    position: 'relative',
    '& .cardBackground': {
      display: 'none'
    },
    [theme.breakpoints.up('md')]: {
      '& .cardBackground': {
        display: 'flex'
      }
    },
    '&.forceDesktopMode': {
      '& .cardBackground': {
        display: 'flex'
      }
    }
  },

  paginationLabel: {
    marginLeft: theme.spacing(2),
    fontSize: 12,
    color: '#333',
    marginTop: theme.spacing(4),
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(0)
    }
  }
})

export default Carousel
